import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import flagIcon from "../assets/images/redFlag.png";
import doubleFlagIcon from "../assets/images/redFlag-Double.png";

const ClientChatHistory = ({ client, onBack }) => {
  const navigate = useNavigate();
  const [expandedChatHistory, setExpandedChatHistory] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [chatDetails, setChatDetails] = useState({});

  useEffect(() => {
    if (client && client.sessions) {
      const processedHistory = client.sessions.map((session) => {
        let summaries = [];

        if (session.summaries && session.summaries.length > 0) {
          // Sort the full summaries if they exist
          summaries = [...session.summaries].sort(
            (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
          );
        } else if (session.summary) {
          // Fallback for old sessions — include start/end times if present
          summaries = [
            {
              timestamp: session.timestamp,
              summary: session.summary,
              sessionDate: session.sessionDate,
              dataTypes: session.dataTypes || {},
              sessionStartTime: session.sessionStartTime || null,
              sessionEndTime: session.sessionEndTime || null,
            },
          ];
        }

        return {
          id: session.sessionID,
          sessionID: session.sessionID,
          date: formatSessionDate(session.timestamp),
          sessionName: session.sessionName,
          summary: extractSummaryText(session.summary),
          details: session.summary,
          concerns: extractConcerns(session.summary),
          timestamp: session.timestamp,
          chat: session.chat || [],
          summaries: summaries,
          totalSummaries: summaries.length,
          finalWorksheets: session.finalWorksheets || null,
        };
      });

      setChatHistory(processedHistory);
    } else {
      setChatHistory([]);
    }
  }, [client]);

  const formatSessionDate = (timestamp) => {
    if (!timestamp) return "Unknown Date";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const extractSummaryText = (fullSummary) => {
    if (!fullSummary) return "No summary available";

    const keyPointsMatch = fullSummary.match(
      /\*\*Key Discussion Points[^:]*:\*\*\s*([^*]+?)(?=\*\*|$)/s
    );
    if (keyPointsMatch) {
      let content = keyPointsMatch[1].trim();
      content = content.replace(/^[-•]\s*/gm, "").trim();
      if (content.length > 20) {
        return content;
      }
    }

    const overviewMatch = fullSummary.match(
      /\*\*Session Overview:\*\*\s*([^*]+?)(?=\*\*|$)/s
    );
    if (overviewMatch) {
      let content = overviewMatch[1].trim();
      if (content.length > 20) {
        return content;
      }
    }

    const engagementMatch = fullSummary.match(
      /\*\*Participant Engagement[^:]*:\*\*\s*([^*]+?)(?=\*\*|$)/s
    );
    if (engagementMatch) {
      let content = engagementMatch[1].trim();
      content = content.replace(/^[-•]\s*/gm, "").trim();
      if (content.length > 20) {
        return content;
      }
    }

    const headerEndMatch = fullSummary.match(
      /\*\*Participant[^:]*:\*\*\s*[^*]+?\*\*([^*]+?)(?=\*\*|$)/s
    );
    if (headerEndMatch) {
      let content = headerEndMatch[1].trim();
      if (content.length > 20) {
        return content;
      }
    }

    const participantMatch = fullSummary.match(
      /\*\*Participant[^:]*:\*\*\s*[^\n]*\n\n([^*]+?)(?=\*\*|$)/s
    );
    if (participantMatch) {
      let content = participantMatch[1].trim();
      if (content.length > 20) {
        return content;
      }
    }

    const cleanedSummary = fullSummary
      .replace(/\*\*[^*]+\*\*/g, "")
      .replace(/\n+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (cleanedSummary.length > 20) {
      return cleanedSummary;
    }

    return "No meaningful summary content found";
  };

  const extractConcerns = (fullSummary) => {
    if (!fullSummary) return [];

    const concerns = [];

    const followUpMatch = fullSummary.match(
      /\*\*Recommended Follow[^:]*:\*\*\s*([^*]+?)(?=\*\*|$)/
    );
    if (followUpMatch) {
      const followUpText = followUpMatch[1];
      const items = followUpText
        .split(/[-•]\s*/)
        .filter((item) => item.trim().length > 0);
      concerns.push(...items.map((item) => item.trim().split(".")[0]));
    }

    const observationsMatch = fullSummary.match(
      /\*\*Notable Progress[^:]*:\*\*\s*([^*]+?)(?=\*\*|$)/
    );
    if (observationsMatch) {
      const obsText = observationsMatch[1];
      if (obsText.length > 10) {
        concerns.push("Progress observation documented");
      }
    }

    if (concerns.length === 0) {
      concerns.push("Ongoing therapeutic support", "Session follow-up needed");
    }

    return concerns.slice(0, 3);
  };

  const WorksheetCanvas = ({ worksheetData, canvasId }) => {
    const canvasRef = React.useRef(null);

    React.useEffect(() => {
      if (!worksheetData || !worksheetData.entries || !canvasRef.current)
        return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const entries = worksheetData.entries;
      let isDrawing = false;

      entries.forEach((entry) => {
        const x = entry.p.X;
        const y = entry.p.Y;
        const color = entry.c;
        const brushSize = entry.b;

        if (x === 0 && y === 0 && brushSize === 0) {
          isDrawing = false;
          return;
        }

        ctx.strokeStyle = `rgba(${color.r * 255}, ${color.g * 255}, ${
          color.b * 255
        }, ${color.a})`;
        ctx.lineWidth = brushSize;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";

        if (!isDrawing) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          isDrawing = true;
        } else {
          ctx.lineTo(x, y);
          ctx.stroke();
        }
      });
    }, [worksheetData]);

    return (
      <canvas
        ref={canvasRef}
        id={canvasId}
        width="800"
        height="1000"
        className="max-w-full h-auto border border-gray-200"
        style={{ maxHeight: "300px" }}
      />
    );
  };

  const renderWorksheets = (finalWorksheets) => {
    if (
      !finalWorksheets ||
      !Array.isArray(finalWorksheets) ||
      finalWorksheets.length === 0
    ) {
      return null;
    }

    const validWorksheets = finalWorksheets.filter((worksheetData) => {
      if (!worksheetData) return false;
      if (typeof worksheetData === "string" && worksheetData.trim() === "")
        return false;
      if (
        typeof worksheetData === "object" &&
        Object.keys(worksheetData).length === 0
      )
        return false;
      return true;
    });

    if (validWorksheets.length === 0) {
      return null;
    }

    return (
      <div className="mb-6 space-y-4">
        <h4 className="text-md font-semibold text-gray-800 mb-3">Canvas</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {validWorksheets.map((worksheetData, index) => {
            const canvasId = `worksheet-canvas-${index}`;
            const worksheetTitle = `Worksheet ${index + 1}`;

            const isBase64 =
              typeof worksheetData === "string" &&
              worksheetData.startsWith("data:image");
            const isS3Url =
              typeof worksheetData === "string" &&
              worksheetData.startsWith("https://");

            return (
              <div
                key={index}
                className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm"
              >
                <h5 className="text-sm font-medium text-gray-700 mb-2">
                  {worksheetTitle}
                </h5>
                <div className="flex justify-center items-center bg-gray-50 rounded overflow-hidden">
                  {isBase64 ? (
                    <img
                      src={worksheetData}
                      alt={worksheetTitle}
                      className="max-w-full h-auto"
                      style={{ maxHeight: "300px" }}
                    />
                  ) : isS3Url ? (
                    <img
                      src={worksheetData}
                      alt={worksheetTitle}
                      className="max-w-full h-auto"
                      style={{ maxHeight: "300px" }}
                    />
                  ) : (
                    <WorksheetCanvas
                      worksheetData={worksheetData}
                      canvasId={canvasId}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const toggleChatHistory = async (sessionId, summaryObj = null) => {
    const chatKey = summaryObj
      ? `${sessionId}_${summaryObj.timestamp}`
      : sessionId;
    const isExpanding = !expandedChatHistory[chatKey];

    setExpandedChatHistory((prev) => ({
      ...prev,
      [chatKey]: isExpanding,
    }));

    if (isExpanding && !chatDetails[chatKey]) {
      try {
        const realChatData = processRealChatData(sessionId, summaryObj);
        setChatDetails((prev) => ({
          ...prev,
          [chatKey]: realChatData,
        }));
      } catch (err) {
        console.error("ClientChatHistory.js: error event");
        setChatDetails((prev) => ({
          ...prev,
          [chatKey]: [],
        }));
      }
    }
  };

  // const processRealChatData = (sessionId, summaryTimestamp = null) => {
  //   const session = chatHistory.find((s) => s.id === sessionId);
  //   if (!session || !session.chat) return [];

  //   let chatMessages = session.chat;

  //   if (summaryTimestamp && session.summaries && session.summaries.length > 1) {
  //     const summaries = [...session.summaries].sort(
  //       (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  //     );

  //     const currentSummaryIndex = summaries.findIndex(
  //       (s) => s.timestamp === summaryTimestamp
  //     );

  //     if (currentSummaryIndex !== -1) {
  //       const currentSummaryTime = new Date(
  //         summaries[currentSummaryIndex].timestamp
  //       );
  //       const previousSummaryTime =
  //         currentSummaryIndex > 0
  //           ? new Date(summaries[currentSummaryIndex - 1].timestamp)
  //           : null;

  //       chatMessages = session.chat.filter((message) => {
  //         try {
  //           const messageTime = parseMessageTimestamp(message.timeStamp);
  //           if (!messageTime) return false;

  //           if (previousSummaryTime) {
  //             return (
  //               messageTime > previousSummaryTime &&
  //               messageTime <= currentSummaryTime
  //             );
  //           } else {
  //             return messageTime <= currentSummaryTime;
  //           }
  //         } catch (e) {
  //           return false;
  //         }
  //       });
  //     }
  //   }

  //   return chatMessages.map((message) => {
  //     let messageType = "client";

  //     if (
  //       message.speaker.includes("Mental Health Companion Nova") ||
  //       message.message.includes("Mental Health Companion Nova:")
  //     ) {
  //       messageType = "ai";
  //     } else if (message.speaker === "system") {
  //       messageType = "system";
  //     } else if (message.message.includes("Joined the Room")) {
  //       messageType = "system";
  //     }

  //     let cleanMessage = message.message;

  //     cleanMessage = cleanMessage.replace(/\u200b/g, "");

  //     if (cleanMessage.includes("Mental Health Companion Nova:")) {
  //       cleanMessage = cleanMessage
  //         .replace("Mental Health Companion Nova:", "")
  //         .trim();
  //     }

  //     let formattedTime = message.timeStamp;
  //     try {
  //       const timestampStr = message.timeStamp.trim();

  //       const parts = timestampStr.split(" ");
  //       if (parts.length >= 3) {
  //         const datePart = parts[0];
  //         const timePart = parts[1];
  //         const ampm = parts[2];

  //         const [month, day, year] = datePart.split("/");

  //         const fullTimeString = `${timePart} ${ampm}`;
  //         const dateString = `${month}/${day}/${year} ${fullTimeString}`;

  //         const date = new Date(dateString);

  //         if (!isNaN(date.getTime())) {
  //           formattedTime = formatDateTime(date);
  //         } else {
  //           formattedTime = message.timeStamp;
  //         }
  //       } else {
  //         formattedTime = message.timeStamp;
  //       }
  //     } catch (e) {
  //       formattedTime = message.timeStamp;
  //     }

  //     return {
  //       type: messageType,
  //       message: cleanMessage,
  //       time: formattedTime,
  //       speaker: message.speaker,
  //       image: message.image,
  //     };
  //   });
  // };

  const processRealChatData = (sessionId, summaryObj = null) => {
  const session = chatHistory.find((s) => s.id === sessionId);
  if (!session || !session.chat) return [];

  if(summaryObj) void 0;

  // Clone messages to avoid mutating
  let chatMessages = [...session.chat];

  // --- ⏱️ Determine window (preserve your logic) ---
  let start = null;
  let end = null;

  if (summaryObj && session.summaries?.length > 0) {
    // try explicit times first
    start =
      summaryObj.sessionStartTime
        ? new Date(summaryObj.sessionStartTime)
        : summaryObj.sessionDate
        ? new Date(summaryObj.sessionDate)
        : null;

    end =
      summaryObj.sessionEndTime
        ? new Date(summaryObj.sessionEndTime)
        : summaryObj.timestamp
        ? new Date(summaryObj.timestamp)
        : null;

    // fallback: prev→current summary
    if ((!start || isNaN(start)) || (!end || isNaN(end))) {
      const sorted = [...session.summaries].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      const idx = sorted.findIndex((s) => s.timestamp === summaryObj.timestamp);
      if (idx !== -1) {
        const prev = idx > 0 ? sorted[idx - 1] : null;
        const cur = sorted[idx];
        if (!start && prev?.timestamp) start = new Date(prev.timestamp);
        if (!end && cur?.timestamp) end = new Date(cur.timestamp);
      }
    }

    // last resort for window (whole chat span)
    if ((!start || isNaN(start)) || (!end || isNaN(end))) {
      const allTimes = (session.chat || [])
        .map((m) => parseMessageTimestamp(m.timeStamp || m.timestamp))
        .filter(Boolean)
        .sort((a, b) => a - b);
      if (allTimes.length > 0) start = allTimes[0];
      if (allTimes.length > 1) end = allTimes[allTimes.length - 1];
    }
  }

  // --- 📸 Inject summary-level worksheet snapshots INSIDE the window ---
  if (summaryObj && Array.isArray(summaryObj.finalWorksheetSnapshots) && summaryObj.finalWorksheetSnapshots.length > 0) {
    // choose a base at end-of-window; if no end, use summary timestamp; else now
    const base = end
      ? new Date(end)
      : parseMessageTimestamp(summaryObj.timestamp) || new Date();

    // place images exactly 1s before end so they stay inside the window and sort to the end
    const placeAt = new Date(base.getTime() - 1000);

    const dividerMessage = {
      type: "system",
      message: "📸 Worksheet Snapshots",
      timeStamp: placeAt.toISOString(),     // UTC for sorting/filtering
      time: formatDateTime(placeAt),         // local display
      speaker: "system",
      divider: true,
    };

    const snapshotMessages = summaryObj.finalWorksheetSnapshots.map((url, idx) => ({
      type: "system",
      message: `Worksheet Snapshot ${idx + 1}`,
      timeStamp: placeAt.toISOString(),     // same end-1s placement
      time: formatDateTime(placeAt),
      speaker: "system",
      image: url,
    }));

    chatMessages.push(dividerMessage, ...snapshotMessages);
  }

  // --- Add session-level final worksheets (only when showing full session) ---
  if (!summaryObj && Array.isArray(session.finalWorksheets) && session.finalWorksheets.length > 0) {
    const base =
      parseMessageTimestamp(session.sessionEndTime) ||
      // fall back to last chat message or now
      (chatMessages.length
        ? parseMessageTimestamp(chatMessages[chatMessages.length - 1].timeStamp || chatMessages[chatMessages.length - 1].timestamp)
        : new Date());
    const placeAt = new Date((base ? base.getTime() : Date.now()) - 1000);

    const finalSessionWorksheets = session.finalWorksheets.map((url, idx) => ({
      type: "system",
      message: `Final Worksheet ${idx + 1}`,
      timeStamp: placeAt.toISOString(),
      time: formatDateTime(placeAt),
      speaker: "system",
      image: url,
    }));

    chatMessages.push(...finalSessionWorksheets);
  }

  // --- ✅ Sort chronologically (UTC-safe) ---
  chatMessages.sort((a, b) => {
    const A = parseMessageTimestamp(a.timeStamp || a.timestamp);
    const B = parseMessageTimestamp(b.timeStamp || b.timestamp);
    return (A?.getTime() || 0) - (B?.getTime() || 0);
  });

  // --- 🧹 Filter messages based on window (if we have one) ---
  if (start && end) {
    chatMessages = chatMessages.filter((m) => {
      const t = parseMessageTimestamp(m.timeStamp || m.timestamp);
      return t && t >= start && t <= end; // all UTC comparisons
    });
  }

  // --- 🧠 Normalize for UI display (ensure visible local-time) ---
  return chatMessages.map((message) => {
    let messageType = "client";
    if (
      message.speaker?.includes("Mental Health Companion Nova") ||
      message.message?.includes("Mental Health Companion Nova:")
    ) {
      messageType = "ai";
    } else if (message.speaker === "system" || message.message?.includes("Joined the Room")) {
      messageType = "system";
    }

    const clean = (message.message || "").replace(/\u200b/g, "").replace("Mental Health Companion Nova:", "").trim();
    const msgTime = parseMessageTimestamp(message.timeStamp || message.timestamp);
    const formattedTime = msgTime ? formatDateTime(msgTime) : (message.timeStamp || message.time || "");

    return {
      type: messageType,
      message: clean,
      time: formattedTime,          // local
      speaker: message.speaker,
      image: message.image,
      moderationFlag: message.moderationFlag,
    };
  });
};




  
// const parseMessageTimestamp = (ts) => {
//   if (!ts) return null;
//   if (ts instanceof Date) return isNaN(ts) ? null : ts;
//   if (typeof ts !== "string") ts = String(ts);

//   ts = ts.trim();
//   let d = null;

//   // 1️⃣ ISO 8601 (with or without zone) — 2025-10-24T16:28:51Z / .000Z
//   if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(?:\.\d+)?)?(Z|[+\-]\d{2}:?\d{2})?$/.test(ts)) {
//     d = new Date(ts);
//     return isNaN(d) ? null : d;
//   }

//   // 2️⃣ Unity/Frontend 12-hour format (US): "MM/DD/YYYY hh:mm[:ss] AM/PM"
//   const match12h = ts.match(
//     /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i
//   );
//   if (match12h) {
//     let [, M, D, Y, h, min, s = "0", ampm] = match12h.map((v) =>
//       typeof v === "string" ? v.trim() : v
//     );
//     M = +M; D = +D; Y = +Y;
//     h = +h; min = +min; s = +s;

//     const isAM = ampm.toLowerCase() === "am";
//     if (!isAM && h !== 12) h += 12;
//     if (isAM && h === 12) h = 0;

//     d = new Date(Date.UTC(Y, M - 1, D, h, min, s));
//     return isNaN(d) ? null : d;
//   }

//   // 3️⃣ 24-hour format (US): "MM/DD/YYYY HH:mm[:ss]"
//   const match24hUS = ts.match(
//     /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
//   );
//   if (match24hUS) {
//     let [, M, D, Y, h, min, s = "0"] = match24hUS.map((v) =>
//       typeof v === "string" ? v.trim() : v
//     );
//     d = new Date(Date.UTC(+Y, +M - 1, +D, +h, +min, +s));
//     return isNaN(d) ? null : d;
//   }

//   // 4️⃣ 24-hour format (EU): "DD/MM/YYYY HH:mm[:ss]"
//   const match24hEU = ts.match(
//     /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
//   );
//   if (match24hEU) {
//     let [, D, M, Y, h, min, s = "0"] = match24hEU.map((v) =>
//       typeof v === "string" ? v.trim() : v
//     );
//     d = new Date(Date.UTC(+Y, +M - 1, +D, +h, +min, +s));
//     return isNaN(d) ? null : d;
//   }

//   // 5️⃣ RFC / GMT style — "Fri, 24 Oct 2025 16:28:51 GMT"
//   d = new Date(ts);
//   if (!isNaN(d)) return d;

//   return null;
// };

const parseMessageTimestamp = (ts) => {
  if (!ts) return null;

  // 🩹 Handle DynamoDB object wrapping
  if (typeof ts === "object" && ts.S) ts = ts.S;

  if (ts instanceof Date) return isNaN(ts) ? null : ts;
  if (typeof ts !== "string") ts = String(ts).trim();

  let d = null;

  // 🩹 Handle ISO (with or without zone)
  if (/^\d{4}-\d{2}-\d{2}/.test(ts)) {
    d = new Date(ts);
    return isNaN(d) ? null : d;
  }

  // 🩹 Handle dd/MM/yyyy HH:mm:ss (e.g. "26/10/2025 16:02:29")
  const euMatch = ts.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );
  if (euMatch) {
    let [, D, M, Y, h, min, s = "0"] = euMatch.map((v) =>
      typeof v === "string" ? v.trim() : v
    );
    d = new Date(Date.UTC(+Y, +M - 1, +D, +h, +min, +s));
    return isNaN(d) ? null : d;
  }

  // 🩹 Handle 12-hour AM/PM format (US)
  const match12h = ts.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i
  );
  if (match12h) {
    let [, M, D, Y, h, min, s = "0", ampm] = match12h.map((v) =>
      typeof v === "string" ? v.trim() : v
    );
    h = +h; min = +min; s = +s;
    const isAM = ampm.toLowerCase() === "am";
    if (!isAM && h !== 12) h += 12;
    if (isAM && h === 12) h = 0;
    d = new Date(Date.UTC(+Y, +M - 1, +D, h, min, s));
    return isNaN(d) ? null : d;
  }

  // 🩹 RFC/GMT fallback
  d = new Date(ts);
  if (!isNaN(d)) return d;

  return null;
};



  const formatDateTime = (dateLike) => {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "Invalid Date";
  return new Intl.DateTimeFormat("en-US", {
    // no timeZone → user's local browser time
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
};





  const getCompleteChatHistory = (sessionId, summaryTimestamp = null) => {
    const chatKey = summaryTimestamp
      ? `${sessionId}_${summaryTimestamp}`
      : sessionId;
    return chatDetails[chatKey] || [];
  };

  const getFlaggedCategories = (moderationData) => {
  try {
    if (!moderationData) return { isFlagged: false, categories: [], isShutdownFlag: false };

    const parsed =
      typeof moderationData === "string" ? JSON.parse(moderationData) : moderationData;

    // Shape A: lockout / shutdown format (top-level flagged + categories)
    if (parsed && typeof parsed === "object" && "categories" in parsed && "flagged" in parsed) {
      const categories = Object.entries(parsed.categories || {})
        .filter(([, v]) => v === true)
        .map(([k]) => k);
      return {
        isFlagged: !!parsed.flagged,
        categories,
        // This indicates the *format*, not whether it’s currently flagged
        isShutdownFlag: true,
      };
    }

    // Shape B: moderation API format ({ results: [ { flagged, categories } ] })
    if (parsed && Array.isArray(parsed.results) && parsed.results.length > 0) {
      const r = parsed.results[0] || {};
      const categories = Object.entries(r.categories || {})
        .filter(([, v]) => v === true)
        .map(([k]) => k);
      return {
        isFlagged: !!r.flagged,
        categories,
        isShutdownFlag: false,
      };
    }

    return { isFlagged: false, categories: [], isShutdownFlag: false };
  } catch (err) {
    console.error("ClientChatHistory.js: error event");
    return { isFlagged: false, categories: [], isShutdownFlag: false };
  }
};





  return (
    <div className="min-h-screen pt-24 bg-white">
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <nav className="flex items-center space-x-2 text-sm">
          <button
            onClick={() => {
              if (onBack) {
                onBack();
              } else {
                navigate("/clients");
              }
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            Clients
          </button>
          <span className="text-gray-400">›</span>
          <span className="text-gray-600">
            {client?.name || "Unknown Client"}
          </span>
        </nav>
      </div>

      <div className="flex">
        <div className="flex-1 p-6">
          {chatHistory.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">
                No session data available.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                The selected session data could not be loaded.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {chatHistory.map((session) => (
                <div
                  key={session.id}
                  className="bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {session.sessionName ||
                            `Session ${session.sessionID.slice(0, 8)}`}
                        </h3>
                      </div>
                    </div>

                    {session.summaries.map((summary, summaryIndex) => {
                      const chatKey = `${session.id}_${summary.timestamp}`;
                      const hasChatForSummary =
                        session.chat && session.chat.length > 0;

                      return (
                        <div
                          key={summaryIndex}
                          className="space-y-3 mt-4 pt-2 border-b border-gray-200 pb-4 last:border-b-0"
                        >
                          <h4 className="text-md font-semibold text-gray-800">
                            {new Date(summary.timestamp).toLocaleString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                                hour12: true,
                              }
                            )}
                          </h4>
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                            <div className="text-sm text-gray-700 leading-relaxed">
                              {summary.summary}
                            </div>
                          </div>

                          <div className="pt-2">
                            <button
                              onClick={() =>
                                toggleChatHistory(session.id, summary)
                              }
                              disabled={!hasChatForSummary}
                              className={`text-sm font-medium ${
                                !hasChatForSummary
                                  ? "text-gray-400 cursor-not-allowed"
                                  : "text-blue-600 hover:text-blue-800"
                              }`}
                            >
                              {expandedChatHistory[chatKey]
                                ? "Hide Interaction History for This Session"
                                : !hasChatForSummary
                                ? "No Interaction History Available"
                                : "View Interaction History for This Session"}
                            </button>
                          </div>

                          {expandedChatHistory[chatKey] && (
                            <div className="mt-4 border-t border-gray-200 pt-4">
                              <h5 className="text-sm font-medium text-gray-900 mb-3">
                                Interaction History
                              </h5>
                              <div className="bg-white border border-gray-300 rounded-lg h-96 overflow-y-auto">
                                <div className="p-4 space-y-4">
                                  {getCompleteChatHistory(
                                    session.id,
                                    summary.timestamp
                                  ).length === 0 ? (
                                    <p className="text-sm text-gray-500 italic">
                                      No chat messages for this session period.
                                    </p>
                                  ) : (
                                    getCompleteChatHistory(
                                      session.id,
                                      summary.timestamp
                                    ).map((message, index) => (
                                      <React.Fragment key={index}>
                                        {message.image && (
                                          <div className="w-[400px]">
                                            <img
                                              src={
                                                message.image.startsWith("http")
                                                  ? message.image
                                                  : `data:image/png;base64,${message.image}`
                                              }
                                              alt="Shared content"
                                              className="rounded-lg shadow-sm"
                                            />
                                          </div>
                                        )}

                                        <div className="flex">
                                          {(() => {
                                            const rawFlagData = message.moderationFlag || message.moderation || null;
                                            // Don't pre-check flagged — let the helper decide
                                            const { isFlagged, categories, isShutdownFlag } = getFlaggedCategories(rawFlagData);

                                            const bubbleClasses = [
                                              "relative max-w-md lg:max-w-xl xl:max-w-5xl 2xl:max-w-7xl px-4 py-2 rounded-lg",
                                              isShutdownFlag
                                                ? "bg-red-700 text-white"
                                                : isFlagged
                                                ? "bg-red-500 text-white"
                                                : message.type === "client"
                                                ? "bg-blue-500 text-white"
                                                : message.type === "ai"
                                                ? "bg-green-100 text-green-800"
                                                : message.type === "system"
                                                ? "bg-gray-100 text-yellow-800 text-xs italic"
                                                : "bg-yellow-50 text-gray-800",
                                            ].join(" ");

                                            const timeColor = isFlagged
                                              ? "text-red-100"
                                              : message.type === "client"
                                              ? "text-blue-100"
                                              : message.type === "ai"
                                              ? "text-green-600"
                                              : message.type === "system"
                                              ? "text-yellow-600"
                                              : "text-gray-500";

                                            return (
                                              <div className={bubbleClasses}>
                                                {/*  Flag icon */}
                                                {isFlagged && (
                                                  <img
                                                    src={isShutdownFlag ? doubleFlagIcon : flagIcon}
                                                    alt="Flagged"
                                                    className="
                                                      absolute 
                                                      -top-2 -left-2 
                                                      w-6 h-6 
                                                      transform -rotate-6 
                                                      drop-shadow-md
                                                    "
                                                  />
                                                )}

                                                {/* Message text */}
                                                <p className="text-sm">
                                                  {message.type !==
                                                    "system" && (
                                                    <strong>
                                                      {message.type === "ai"
                                                        ? "AI-MH"
                                                        : message.speaker}
                                                      :
                                                    </strong>
                                                  )}{" "}
                                                  {message.message}
                                                </p>

                                                {/* Timestamp */}
                                                <p
                                                  className={`text-xs mt-1 ${timeColor}`}
                                                >
                                                  {message.time}
                                                </p>

                                                {/* Moderation details */}
                                                {isFlagged && (
                                                  <div
                                                    className={`mt-2 text-xs border p-2 rounded ${
                                                      isShutdownFlag
                                                        ? "bg-red-800 bg-opacity-40 border-red-500"
                                                        : "bg-red-600 bg-opacity-20 border-red-400"
                                                    }`}
                                                  >
                                                    <p className="font-semibold text-white mb-1">
                                                      {isShutdownFlag
                                                        ? "Session Shutdown Triggered"
                                                        : "Flagged Categories:"}
                                                    </p>

                                                    {categories.length > 0 && (
                                                      <ul className="flex flex-wrap gap-1">
                                                        {categories.map(
                                                          (cat) => (
                                                            <li
                                                              key={cat}
                                                              className="bg-red-700 text-white rounded px-2 py-0.5 text-[11px]"
                                                            >
                                                              {cat}
                                                            </li>
                                                          )
                                                        )}
                                                      </ul>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </React.Fragment>
                                    ))
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {session.finalWorksheets && (
                      <div className="px-4 pt-4">
                        {renderWorksheets(session.finalWorksheets)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientChatHistory;
